import { ChangeDetectionStrategy, Component, computed, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";

import { map, timer } from "rxjs";
import { SvgIconComponent } from "angular-svg-icon";
import { intervalToDuration } from "date-fns";

import { AuthService } from "@core/services/auth.service";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { adminRoutes } from "@features/admin/routes/admin.routes";
import { AdminRoutesConstants } from "@features/admin/routes/admin.routes.constants";
import { LoginRoutesConstants } from "@features/login/routes/login.routes.constants";
import { NotificationsComponent } from "@features/notifications/notifications.component";
import { OrganizationRoutesConstants } from "@features/organization/routes/organization.routes.constants";
import { organizationAdminRoutes } from "@features/organization/routes/organization-admin.routes";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { PlanType } from "@shared/models/plan.model";
import { UserRole } from "@shared/models/user.model";

import { UserProfilePopupComponent } from "./user-profile-popup.component";

interface NavItem {
  label: string;
  route: string[];
  exact?: boolean;
}

@Component({
  selector: "component-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    SvgIconComponent,
    UserProfilePopupComponent,
    CustomIconComponent,
    NotificationsComponent
  ]
})
export class HeaderComponent {
  private readonly _authService = inject(AuthService);
  private readonly _modalService = inject(ModalService);
  private readonly _router = inject(Router);

  private _expiredModalRef: NgbModalRef | null = null; // Track if expired modal is currently open

  // Detect public view by checking if URL starts with /public
  readonly isPublicView = toSignal(this._router.events.pipe(map(() => this._router.url.startsWith("/public"))), {
    initialValue: this._router.url.startsWith("/public")
  });

  protected readonly user = computed(() => this._authService.currentUser());
  protected readonly isImpersonating = computed(() => this._authService.isImpersonating());
  protected readonly documentationRoutes = DocumentationRoutesConstants;
  protected readonly UserRole = UserRole;

  protected readonly navItems = computed<NavItem[]>(() => {
    const user = this.user();
    if (!user) return [];

    const items: NavItem[] = [
      {
        label: "Documents",
        route: [DocumentationRoutesConstants.main()],
        exact: false
      }
    ];

    if (user.role === UserRole.SUPER_ADMIN) {
      if (adminRoutes.children) {
        adminRoutes.children.forEach((child) => {
          if (child.title && child.path) {
            items.push({
              label: child.title as string,
              route: [AdminRoutesConstants.MAIN, child.path]
            });
          }
        });
      }
    } else if (user.role === UserRole.ORGANIZATION_ADMIN) {
      if (organizationAdminRoutes.children) {
        organizationAdminRoutes.children.forEach((child) => {
          if (child.title && child.path && !child.path.includes("**")) {
            // Skip API Clients tab if organization doesn't have API enabled
            if (child.path === 'api-clients' && !user.organization?.apiEnabled) {
              return;
            }
            
            items.push({
              label: child.title as string,
              route: ["organization", child.path]
            });
          }
        });
      }
    } else if (user.role === UserRole.ORGANIZATION_USER) {
      // Org users should still see "My Organization" entry
      items.push({
        label: "My Organization",
        route: ["organization", OrganizationRoutesConstants.MY_ORGANIZATION]
      });
    }

    return items;
  });

  protected readonly isTemporaryUser = computed(() => this.user()?.isTemporary ?? false);

  protected readonly showUpgradeButton = computed(() => {
    const user = this.user();
    if (!user || user.isTemporary) {
      return false;
    }
    const planType = user.organization?.plan?.type;
    return !planType || planType === PlanType.FREE;
  });

  // Timer that updates every minute for temporary user expiration countdown
  private readonly _timer = toSignal(timer(0, 10000)); // 10000ms = 10 seconds

  protected readonly isExpired = computed(() => {
    if (!this.isTemporaryUser()) {
      return false;
    }

    this._timer(); // Re-run this computed signal every 10 seconds

    const user = this.user();
    const org = user?.organization;
    const plan = org?.plan;

    const expirationDate = this.getExpirationDate(user, org, plan);
    if (!expirationDate) {
      return false;
    }

    const now = new Date();
    return now > expirationDate;
  });

  protected readonly expirationMessage = computed(() => {
    // Only trigger timer updates when user is temporary
    if (!this.isTemporaryUser()) {
      return "";
    }

    this._timer(); // Re-run this computed signal every minute
    const user = this.user();
    const org = user?.organization;
    const plan = org?.plan;

    const expirationDate = this.getExpirationDate(user, org, plan);
    if (!expirationDate) {
      return "";
    }

    const now = new Date();

    if (now > expirationDate) {
      return "expired";
    }

    const duration = intervalToDuration({ start: now, end: expirationDate });
    const parts = [];
    if (duration.days) parts.push(`${duration.days} day${duration.days > 1 ? "s" : ""}`);
    if (duration.hours) parts.push(`${duration.hours} hour${duration.hours > 1 ? "s" : ""}`);

    if (parts.length > 0) {
      return `Guest account active – expires in ${parts.join(" ")}`;
    }

    if (duration.minutes) {
      return `Guest account active – expires in ${duration.minutes} minute${duration.minutes > 1 ? "s" : ""}`;
    }

    return `Guest account active – expires in less than a minute`;
  });

  constructor() {
    // Monitor expiration and show modal automatically when session expires
    // This will show the modal every time the app loads/refreshes while expired
    effect(() => {
      const expired = this.isExpired();
      const user = this.user();

      // Show expired modal every time when expired, but prevent duplicate modals
      if (expired && user?.isTemporary && !this._expiredModalRef) {
        this._expiredModalRef = this._modalService.openUpgradeModel(user, true);

        // Clear reference when modal closes (if user upgrades)
        this._expiredModalRef?.result.then(
          () => {
            this._expiredModalRef = null;
          },
          () => {
            this._expiredModalRef = null;
          }
        );
      }
    });
  }

  // Helper to calculate expiration date
  private getExpirationDate(user: any, org: any, plan: any): Date | null {
    if (!user?.isTemporary || !org || !plan || !plan.expirationPeriod || !plan.expirationUnit) {
      return null;
    }

    const creationDate = new Date(org.planSubscribedAt!);
    const expirationDate = new Date(creationDate);

    if (plan.expirationUnit === "DAYS") {
      expirationDate.setDate(creationDate.getDate() + plan.expirationPeriod);
    } else if (plan.expirationUnit === "MONTHS") {
      expirationDate.setMonth(creationDate.getMonth() + plan.expirationPeriod);
    } else if (plan.expirationUnit === "HOURS") {
      expirationDate.setHours(creationDate.getHours() + plan.expirationPeriod);
    } else if (plan.expirationUnit === "MINUTES") {
      expirationDate.setMinutes(creationDate.getMinutes() + plan.expirationPeriod);
    }

    return expirationDate;
  }

  protected onSignUpClick(): void {
    // If in public view mode, navigate to signup page
    if (this.isPublicView()) {
      this._router.navigate([LoginRoutesConstants.signup()]);
      return;
    }

    // Otherwise, show upgrade modal for authenticated users
    const currentUser = this.user();
    if (currentUser) {
      this._modalService.openUpgradeModel(currentUser, this.isExpired());
    }
  }

  protected onLogoClick(): void {
    this._router.navigate([DocumentationRoutesConstants.main()]);
  }

  protected onStopImpersonating(): void {
    this._authService.stopImpersonating();
  }
}
